/**
 * rb-import.js — heuristic "paste your existing resume" importer (BETA).
 *
 * Parses pasted plain-text resume content into a ResumeModel: extracts
 * contacts by regex (high confidence), the name/title from the opening lines,
 * and splits the body on recognised section headings (9 UI languages). It is
 * deliberately labelled beta — the UI must tell the user to check every field.
 *
 * Depends on: RbState. globalThis.RbImport
 */
(function () {
    'use strict';

    var S = function () { return globalThis.RbState; };

    /* section-heading keywords, lowercased, per the 9 UI locales */
    var SECTION_WORDS = {
        summary: ['summary', 'profile', 'about', 'objective', 'о себе', 'профиль', 'zusammenfassung',
            'kurzprofil', 'perfil', 'profil', 'sobre', 'resumen', '요약', '자기소개', 'نبذة', 'ringkasan'],
        experience: ['experience', 'work experience', 'employment', 'work history', 'опыт', 'опыт работы',
            'berufserfahrung', 'erfahrung', 'experiencia', 'expérience', 'experiência', 'experiencia laboral',
            '경력', 'خبرة', 'الخبرة', 'pengalaman', 'pengalaman kerja'],
        education: ['education', 'образование', 'ausbildung', 'bildung', 'educación', 'formación', 'formation',
            'educação', 'formação', '학력', 'تعليم', 'التعليم', 'pendidikan'],
        skills: ['skills', 'навыки', 'kenntnisse', 'fähigkeiten', 'habilidades', 'compétences', 'competências',
            'aptitudes', '기술', '보유 기술', 'مهارات', 'المهارات', 'keterampilan'],
        languages: ['languages', 'языки', 'sprachen', 'idiomas', 'langues', '언어', 'لغات', 'اللغات', 'bahasa'],
        certifications: ['certifications', 'certificates', 'сертификаты', 'zertifikate', 'certificaciones',
            'certificats', 'certificações', '자격증', 'شهادات', 'sertifikasi'],
        projects: ['projects', 'проекты', 'projekte', 'proyectos', 'projets', 'projetos', '프로젝트', 'مشاريع', 'proyek']
    };

    var RE_EMAIL = /[\w.+-]+@[\w-]+\.[\w.-]+/;
    var RE_PHONE = /(\+?\d[\d().\s-]{7,}\d)/;
    var RE_URL = /((?:https?:\/\/)?(?:www\.)?[\w-]+\.[a-z]{2,}(?:\/[\w./%#?=&-]*)?)/i;
    var RE_DATERANGE = /((?:19|20)\d\d)\s*[–—\-to.]+\s*((?:19|20)\d\d|present|now|current|настоящее|heute|actuel|actual|atual|現在|현재|الآن|sekarang)/i;
    var BULLET = /^[\s]*[•·▪◦*\-–—]\s+/;

    function clean(s) { return String(s || '').replace(/\s+/g, ' ').trim(); }

    /* classify a line as a section heading → returns the section key or null */
    function headingOf(line) {
        var t = line.toLowerCase().replace(/[:：]\s*$/, '').trim();
        if (!t || t.length > 42) return null;
        for (var sec in SECTION_WORDS) {
            var words = SECTION_WORDS[sec];
            for (var i = 0; i < words.length; i++) {
                if (t === words[i] || t.indexOf(words[i]) === 0) return sec;
            }
        }
        return null;
    }

    /* split a heading block's lines into blank-line-separated groups */
    function groups(lines) {
        var out = [], cur = [];
        lines.forEach(function (l) {
            if (l.trim() === '') { if (cur.length) { out.push(cur); cur = []; } }
            else cur.push(l);
        });
        if (cur.length) out.push(cur);
        return out;
    }

    function parse(text, region) {
        var model = S().createFreshState(region || 'us');
        var raw = String(text || '').split(/\r?\n/);
        var p = model.personal;

        /* ---- contacts (scan the whole text) ---- */
        var joined = raw.join('\n');
        var m;
        if ((m = joined.match(RE_EMAIL))) p.email = m[0];
        if ((m = joined.match(RE_PHONE))) p.phone = clean(m[1]);
        raw.forEach(function (l) {
            var low = l.toLowerCase();
            if (!p.linkedin && low.indexOf('linkedin.') > -1) { var u = l.match(RE_URL); if (u) p.linkedin = clean(u[1]); }
            else if (!p.github && low.indexOf('github.') > -1) { var g = l.match(RE_URL); if (g) p.github = clean(g[1]); }
        });

        /* ---- name + title: first meaningful non-contact lines ---- */
        var headStart = 0;
        for (var i = 0; i < raw.length && i < 8; i++) {
            var ln = clean(raw[i]);
            if (!ln) continue;
            if (RE_EMAIL.test(ln) || RE_PHONE.test(ln) || headingOf(ln)) continue;
            if (!p.name && ln.length <= 60 && /[\p{L}]/u.test(ln)) { p.name = ln; headStart = i + 1; continue; }
            if (p.name && !p.title && ln.length <= 70 && !RE_URL.test(ln)) { p.title = ln; headStart = i + 1; break; }
            break;
        }

        /* ---- split body into heading blocks ---- */
        var blocks = [];        /* {sec|null|'_intro', lines:[]} */
        var cur = { sec: '_intro', lines: [] };
        for (var j = headStart; j < raw.length; j++) {
            var h = headingOf(clean(raw[j]));
            if (h) { if (cur.lines.length) blocks.push(cur); cur = { sec: h, title: clean(raw[j]), lines: [] }; }
            else cur.lines.push(raw[j]);
        }
        if (cur.lines.length) blocks.push(cur);

        blocks.forEach(function (b) {
            var body = b.lines.map(function (l) { return l; });
            var textJoined = body.join('\n').trim();
            if (!textJoined) return;

            if (b.sec === 'summary' || (b.sec === '_intro' && !model.summary)) {
                /* intro paragraph before any heading, or an explicit summary */
                var para = body.map(clean).filter(Boolean).join(' ');
                if (para && para.length > 20) model.summary = (model.summary ? model.summary + ' ' : '') + para;
                return;
            }
            if (b.sec === 'skills') {
                textJoined.split(/[,;\n•·]+/).map(clean).filter(function (s) { return s && s.length < 40; })
                    .slice(0, 30).forEach(function (name) {
                        model.skills.push(Object.assign(S().newEntry('skills'), { name: name }));
                    });
                return;
            }
            if (b.sec === 'languages') {
                textJoined.split(/[,;\n•·]+/).map(clean).filter(Boolean).slice(0, 12).forEach(function (name) {
                    model.languages.push(Object.assign(S().newEntry('languages'), { name: name.replace(/\(.*\)/, '').trim() }));
                });
                return;
            }
            if (b.sec === 'experience' || b.sec === 'education' || b.sec === 'projects') {
                groups(body).forEach(function (grp) {
                    var first = clean(grp[0]);
                    var rest = grp.slice(1);
                    var dr = first.match(RE_DATERANGE) || textJoined.match(RE_DATERANGE);
                    var head = first.replace(RE_DATERANGE, '').replace(/[|·•]/g, ' ').trim();
                    var parts = head.split(/\s+(?:—|–|-|@|at|bei)\s+/i);
                    /* «Менеджер в Яндексе»: «в» names the company only before a capitalised
                       name, and the last such «в» — «Специалист в области продаж» stays one
                       position. (It used to be in the split above between two word
                       boundaries, which never matched: JavaScript's \b knows only Latin.) */
                    if (parts.length === 1) {
                        var vRe = /\s+в\s+(?=[«"„A-ZА-ЯЁ])/g, vm, vLast = null;
                        while ((vm = vRe.exec(head))) vLast = vm;
                        if (vLast && vLast.index > 0) parts = [head.slice(0, vLast.index), head.slice(vLast.index + vLast[0].length)];
                    }
                    var bullets = rest.filter(function (l) { return BULLET.test(l); })
                        .map(function (l) { return l.replace(BULLET, '').trim(); });
                    var descr = rest.filter(function (l) { return l.trim() && !BULLET.test(l); })
                        .map(clean).join(' ');
                    if (b.sec === 'experience') {
                        model.experience.push(Object.assign(S().newEntry('experience'), {
                            position: parts[0] || head, company: parts[1] || '',
                            startDate: dr ? dr[1] : '', endDate: (dr && !/present|now|current|настоящ|heute|actu|atual|現在|현재|الآن|sekarang/i.test(dr[2])) ? dr[2] : '',
                            current: !!(dr && /present|now|current|настоящ|heute|actu|atual|現在|현재|الآن|sekarang/i.test(dr[2])),
                            description: descr, achievements: bullets.join('\n')
                        }));
                    } else if (b.sec === 'education') {
                        model.education.push(Object.assign(S().newEntry('education'), {
                            degree: parts[0] || head, institution: parts[1] || '',
                            startDate: dr ? dr[1] : '', endDate: dr ? dr[2] : '', honors: bullets.join(', ')
                        }));
                    } else {
                        model.projects.push(Object.assign(S().newEntry('projects'), {
                            name: parts[0] || head, description: descr || bullets.join(' ')
                        }));
                    }
                });
                return;
            }
            if (b.sec === 'certifications') {
                body.map(clean).filter(Boolean).slice(0, 20).forEach(function (l) {
                    model.certifications.push(Object.assign(S().newEntry('certifications'), { name: l.replace(BULLET, '') }));
                });
                return;
            }
            /* unrecognised heading → custom section */
            if (b.sec === '_intro') return;
            model.custom.push({ title: b.title || '', items: body.map(clean).filter(Boolean) });
        });

        return model;
    }

    globalThis.RbImport = { parse: parse, headingOf: headingOf };
})();
